/**
 * step-channels.tsx — Bước 4: giao kênh & giờ làm (nhóm "Vận hành", lưu ngay bằng `PUT /ai/bots/:id`).
 *  - Checkbox kênh đã kết nối; kênh đang thuộc nhân viên AI khác ghi "đang do <tên> phụ trách — chọn để chuyển sang"
 *    (máy chủ tự gỡ khỏi nhân viên kia khi lưu); kênh do nhân viên AI mặc định phục vụ ghi "<tên> (mặc định)".
 *  - Chưa có kênh → "Kết nối kênh"; có rồi → "Kết nối thêm kênh" — đều mở màn Kết nối ở TAB MỚI (bước này giữ nguyên);
 *    nút "Tải lại" chỉ tải lại danh sách kênh + trạng thái kết nối (cũng tự làm mới khi quay lại tab).
 *  - Giờ làm: Trong giờ gợi ý, ngoài giờ tự động (08:00–18:00 — mặc định đã chốt) / Tự động 24/7 / Tuỳ chỉnh.
 * Nhân viên AI còn TẮT tới bước Ra mắt, nên giao kênh ở đây chưa làm AI trả lời khách.
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Loader2, PlugZap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PlatformTag } from '@/components/shared/platform-mark'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useAiBots } from '@/hooks/use-ai'
import { useInboxAccounts } from '@/hooks/use-integrations'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { CONNECT_CHANNELS_PATH } from '@/components/ai/open-target-lib'
import { updateWizardBot, type WizardBot } from './wizard-api'
import {
  HOURS_PRESETS, channelOptions, customHoursProblem, hoursSummary, presetOf, scheduleFields, stolenChannels,
  type CustomHours, type HoursPreset,
} from './wizard-lib'

const HOURS = Array.from({ length: 24 }, (_, h) => h)
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`

export function StepChannels({ bot, onSaved, onBack }: {
  bot: WizardBot
  onSaved: () => Promise<unknown>
  onBack: () => void
}) {
  const { t } = useTranslation('aiWizard')
  const channelsQ = useInboxAccounts()
  const botsQ = useAiBots()
  const { data: channels, isLoading } = channelsQ
  const { data: botsData } = botsQ
  const options = useMemo(
    () => channelOptions(channels ?? [], botsData?.bots ?? [bot], bot.id),
    [channels, botsData, bot],
  )
  const [selected, setSelected] = useState<string[]>(() => [...(bot.channelAccountIds ?? [])])
  const initial = useMemo(() => presetOf(bot), [bot])
  const [preset, setPreset] = useState<HoursPreset>(initial.preset)
  const [custom, setCustom] = useState<CustomHours>(initial.custom)
  const [saving, setSaving] = useState(false)
  const fields = scheduleFields(preset, custom)
  const problem = preset === 'custom' ? customHoursProblem(custom) : null
  const stolen = stolenChannels(options, selected)

  function toggle(id: string, on: boolean) {
    setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)))
  }

  async function save() {
    if (problem) { toast.error(problem); return }
    setSaving(true)
    try {
      // Chỉ gửi kênh còn tồn tại trong danh sách (kênh đã xoá tự rơi ra).
      const ids = selected.filter((id) => options.some((o) => o.id === id))
      await updateWizardBot(bot.id, { channelAccountIds: ids, ...fields })
      await onSaved()
      if (stolen.length) toast.success(t('channels.moved', { count: stolen.length, name: bot.name }))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-medium">{t('channels.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('channels.intro', { name: bot.name })}</p>
      </div>

      <section className="space-y-2 rounded-xl border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-sm">{t('channels.responsible')}</Label>
          <div className="flex flex-wrap items-center gap-1">
            <ReloadButton onReload={() => Promise.all([channelsQ.refetch(), botsQ.refetch()])} what={t('channels.listWhat')} />
            {options.length > 0 && (
              <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="outline" size="sm" title={t('channels.openConnectTitle')}>
                <PlugZap className="h-3.5 w-3.5" /> {t('channels.connectMore')}
              </OpenInNewTab>
            )}
          </div>
        </div>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">{t('channels.loading')}</p>
        ) : options.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <PlugZap className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm">{t('channels.noneConnected')}</p>
            <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="default" size="sm" title={t('channels.openConnectTitle')}><PlugZap className="h-4 w-4" /> {t('channels.connect')}</OpenInNewTab>
            <p className="text-[11px] text-muted-foreground">{t('channels.connectHint')}</p>
          </div>
        ) : (
          <>
            <ul className="divide-y rounded-lg border">
              {options.map((o) => (
                <li key={o.id}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2">
                    <Checkbox className="mt-0.5" checked={selected.includes(o.id)} onCheckedChange={(v) => toggle(o.id, v === true)} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2 text-sm">
                        <PlatformTag platform={o.platform} />
                        <span className="truncate font-medium">{o.name}</span>
                        {!o.connected && <span className="text-[11px] text-destructive">{t('common:status.disconnected')}</span>}
                      </span>
                      {o.note && <span className={cn('mt-0.5 block text-xs', o.state === 'other' ? 'text-warning' : 'text-muted-foreground')}>{o.note}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground">
              {t('channels.unselectedHint')}
            </p>
          </>
        )}
      </section>

      <section className="space-y-3 rounded-xl border p-4">
        <Label className="text-sm">{t('channels.hoursAndMode')}</Label>
        <div className="space-y-2" role="radiogroup" aria-label={t('channels.hours')}>
          {HOURS_PRESETS.map((p) => (
            <label key={p.value} className={cn('flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5', preset === p.value && 'border-primary bg-primary/5')}>
              <input type="radio" name="wz-hours" className="mt-1 accent-[hsl(var(--primary))]" checked={preset === p.value} onChange={() => setPreset(p.value)} />
              <span>
                <span className="block text-sm font-medium">{p.title}</span>
                <span className="block text-xs text-muted-foreground">{p.desc}</span>
              </span>
            </label>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="grid gap-3 rounded-lg bg-muted/40 p-3 sm:grid-cols-2">
            <HourSelect label={t('channels.workStart')} value={custom.workStart} onChange={(v) => setCustom({ ...custom, workStart: v })} />
            <HourSelect label={t('channels.workEnd')} value={custom.workEnd} onChange={(v) => setCustom({ ...custom, workEnd: v })} />
            <div className="space-y-1">
              <Label className="text-xs">{t('channels.inHours')}</Label>
              <Select value={custom.inHoursMode} onValueChange={(v) => setCustom({ ...custom, inHoursMode: v as CustomHours['inHoursMode'] })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="suggest">{t('channels.inSuggest')}</SelectItem>
                  <SelectItem value="manual">{t('channels.inManual')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('channels.afterHours')}</Label>
              <Select value={custom.afterHoursMode} onValueChange={(v) => setCustom({ ...custom, afterHoursMode: v as CustomHours['afterHoursMode'] })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{t('channels.afterAuto')}</SelectItem>
                  <SelectItem value="suggest">{t('channels.afterSuggest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {problem && <p className="text-xs text-destructive sm:col-span-2">{problem}</p>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('channels.summary', { summary: hoursSummary(fields) })}</p>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}</Button>
        <div className="flex items-center gap-2">
          {selected.length === 0 && options.length > 0 && <span className="text-xs text-muted-foreground">{t('channels.noneSelected')}</span>}
          <Button onClick={() => void save()} disabled={saving || !!problem}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} {t('channels.saveNext')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function HourSelect({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hh(h)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  )
}
